/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Detailed view of individual bike information. Shows maintenance history,
ride statistics, and current status. Provides actions for bike management.
*/

import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabaseBikeService } from "@/api/supabaseBikeService";
import { BikeDetail } from "./types/bikeTypes";
import BikeHeader from "./components/BikeHeader";
import BikeDetailsTab from "./components/BikeDetailsTab";
import BikeRideHistory from "./components/BikeRideHistory";
import MaintenanceTabContent from "./components/MaintenanceTabContent";

const BikeDetailPage: React.FC = () => {
  const { bikeId } = useParams<{ bikeId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'details';
  
  if (!bikeId) {
    React.useEffect(() => {
      console.error("No eBike ID provided in URL parameters");
      toast.error("No eBike ID provided. Redirecting to eBikes list.");
      navigate("/bikes");
    }, [navigate]);
    return null;
  }
  
  const bikesService = supabaseBikeService;
  
  const {
    data: bike,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["bike", bikeId],
    queryFn: async () => {
      // Fetch bike with extended details including user information if at a home location
      const bikeData = await bikesService.getBike(bikeId);
      
      // Fetch user info if the bike has a user_location_id
      if (bikeData.userLocationId) {
        try {
          // Get detailed bike data including the user information
          const { data } = await supabaseBikeService.getBikeWithUserDetails(bikeId);
          if (data && data.users?.username) {
            // Add userName to the bike details
            return {
              ...bikeData,
              userName: data.users.username
            };
          }
        } catch (userError) {
          console.error("Error fetching user details:", userError);
        }
      }
      
      return bikeData;
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });
  
  React.useEffect(() => {
    if (error) {
      console.error(`Error fetching bike ${bikeId}:`, error);
      toast.error(`Failed to load bike details: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }, [error, bikeId]);

  const handleDeleteBike = async () => {
    try {
      await bikesService.deleteBike(bikeId);
      navigate("/bikes");
    } catch (error) {
      console.error(`Error deleting bike ${bikeId}:`, error);
      toast.error(`Failed to delete bike: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };
  
  if (isLoading) {
    return null;
  }
  
  if (!bike) {
    return null;
  }
  
  return (
    <div className="space-y-6">
      <BikeHeader bike={bike} onDeleteBike={handleDeleteBike} />
      
      <Tabs defaultValue={defaultTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="rides">Ride History</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details">
          <BikeDetailsTab bike={bike} />
        </TabsContent>
        
        <TabsContent value="rides">
          <BikeRideHistory bikeId={bikeId} />
        </TabsContent>
        
        <TabsContent value="maintenance">
          <MaintenanceTabContent bikeId={bikeId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BikeDetailPage;
